import { PhoneForwarded, Refresh, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { usePhoneCalls } from '../../../../contexts/PhoneCallsContext/PhoneCallsContext';
import {
  AgentAvailabilityStatus,
  AgentDirectoryEntry,
  Conference,
  ParticipantType,
} from '../../../../interfaces/calls';
import { TransferConfirmationModal } from '../TransferConfirmationModal/TransferConfirmationModal';
import { TransferStatusModal } from '../TransferStatusModal/TransferStatusModal';

interface DirectoryProps {
  agents: AgentDirectoryEntry[];
  currentAgent: Omit<AgentDirectoryEntry, 'status'>;
  conference?: Conference | null;
  isLoading?: boolean;
  lastUpdatedAt?: Date | null;
  onWarmTransfer?: (agent: AgentDirectoryEntry) => void;
  onBlindTransfer?: (agent: AgentDirectoryEntry) => void;
  onRefresh?: () => void;
}

const formatLastUpdated = (lastUpdatedAt: Date | null | undefined, nowMs: number): string => {
  if (!lastUpdatedAt) {
    return 'Updating…';
  }
  const diffSeconds = Math.max(0, Math.floor((nowMs - lastUpdatedAt.getTime()) / 1000));
  if (diffSeconds < 5) {
    return 'Updated just now';
  }
  if (diffSeconds < 60) {
    return `Updated ${diffSeconds}s ago`;
  }
  const diffMinutes = Math.floor(diffSeconds / 60);
  return `Updated ${diffMinutes}m ago`;
};

const getStatusColor = (status: AgentAvailabilityStatus): string => {
  switch (status) {
    case AgentAvailabilityStatus.AVAILABLE:
      return '#4caf50';
    case AgentAvailabilityStatus.ON_CALL:
      return '#4caf50';
    case AgentAvailabilityStatus.AWAY:
      return '#ff9800';
    case AgentAvailabilityStatus.DO_NOT_DISTURB:
      return '#f44336';
    case AgentAvailabilityStatus.OFFLINE:
      return '#9e9e9e';
    case AgentAvailabilityStatus.DISABLED:
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
};

export const Directory: FC<DirectoryProps> = ({
  agents,
  currentAgent,
  conference,
  isLoading = false,
  lastUpdatedAt,
  onWarmTransfer,
  onBlindTransfer,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentDirectoryEntry | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingTransferType, setPendingTransferType] = useState<'WARM' | 'BLIND' | null>(null);

  // Tick once per second so the "Updated Xs ago" label stays current without re-fetching.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);
  const lastUpdatedLabel = formatLastUpdated(lastUpdatedAt, nowMs);

  const { transferData, cancelTransfer } = usePhoneCalls();

  // Show transfer status modal during warm transfers (including failed state)
  const showTransferStatusModal =
    transferData !== null &&
    transferData.transferType === 'WARM' &&
    (transferData.status === 'initiated' ||
      transferData.status === 'ringing' ||
      transferData.status === 'failed');

  const isTransferError = transferData?.status === 'failed';

  const handleCloseTransferModal = () => {
    // Clear transfer data to close the modal
    cancelTransfer();
  };

  // Get agents currently in the conference (excluding current agent)
  const agentsOnCall = useMemo(() => {
    if (!conference?.participants) {
      return [];
    }

    // Get agent core provider IDs from conference participants
    const agentCoreProviderIds = conference.participants
      .filter(
        (p) =>
          p.participantType === ParticipantType.Agent &&
          (p.status === 'JOINED' || p.status === 'JOINING') &&
          p.coreProviderId !== currentAgent.coreProviderId,
      )
      .map((p) => p.coreProviderId);

    // Find matching agents in the directory
    return agents.filter((agent) => agentCoreProviderIds.includes(agent.coreProviderId));
  }, [conference, agents, currentAgent]);

  // Filter agents by search term (excluding current agent and agents on call)
  const filteredAgents = useMemo(() => {
    const agentCoreProviderIdsOnCall = agentsOnCall.map((a) => a.coreProviderId);

    let availableAgents = agents.filter(
      (agent) =>
        agent.coreProviderId !== currentAgent.coreProviderId &&
        !agentCoreProviderIdsOnCall.includes(agent.coreProviderId),
    );

    // Then apply search filter
    if (!searchTerm.trim()) {
      return availableAgents;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return availableAgents.filter((agent) => agent.name?.toLowerCase().includes(lowerSearchTerm));
  }, [agents, searchTerm, currentAgent.coreProviderId, agentsOnCall]);

  const handleAgentClick = (agent: AgentDirectoryEntry) => setSelectedAgent(agent);

  const handleWarmTransferClick = useCallback(() => {
    if (selectedAgent) {
      setPendingTransferType('WARM');
      setShowConfirmModal(true);
    }
  }, [selectedAgent]);

  const handleBlindTransferClick = useCallback(() => {
    if (selectedAgent) {
      setPendingTransferType('BLIND');
      setShowConfirmModal(true);
    }
  }, [selectedAgent]);

  const handleConfirmTransfer = useCallback(() => {
    if (selectedAgent && pendingTransferType === 'WARM' && onWarmTransfer) {
      onWarmTransfer(selectedAgent);
    } else if (selectedAgent && pendingTransferType === 'BLIND' && onBlindTransfer) {
      onBlindTransfer(selectedAgent);
    }
    setShowConfirmModal(false);
    setPendingTransferType(null);
  }, [selectedAgent, pendingTransferType, onWarmTransfer, onBlindTransfer]);

  const handleCancelTransfer = useCallback(() => {
    setShowConfirmModal(false);
    setPendingTransferType(null);
  }, []);

  return (
    <Box
      sx={{
        width: '320px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
      }}>
      {/* Header */}
      <Box
        sx={{
          paddingTop: '7px',
          paddingBottom: '7px',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          display: 'flex',
          alignItems: 'center',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='subtitle1'>Directory</Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ fontSize: '11px', lineHeight: 1 }}>
            {lastUpdatedLabel}
          </Typography>
        </Box>
        {onRefresh && (
          <IconButton
            size='small'
            onClick={onRefresh}
            aria-label='Refresh directory'
            disabled={isLoading}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(26, 102, 122, 0.08)',
              },
            }}>
            <Refresh sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
      </Box>

      {/* Search Input */}
      <Box sx={{ p: 1, px: 2, marginBottom: '10px' }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Type to search an agent'
          value={searchTerm}
          disabled={showConfirmModal || showTransferStatusModal || showTransferStatusModal}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            sx: {
              borderRadius: '30px',
              border: '1px solid #F5F5F5',
              fontSize: '14px',
            },
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* On Call Section */}
      {agentsOnCall.length > 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant='subtitle2'
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
              mb: 1,
            }}>
            Agents On Call
          </Typography>
          <List sx={{ p: 0 }}>
            {agentsOnCall.map((agent) => {
              const phoneNumber =
                agent.trackingNumbers?.incoming?.[0]?.number ||
                agent.trackingNumbers?.outgoing?.[0]?.number ||
                agent.phoneNumber ||
                '';

              return (
                <ListItem
                  key={agent.id}
                  disablePadding
                  sx={{
                    pt: '0',
                    pb: '0',
                    pl: '5px',
                    pr: '5px',
                    mb: '8px',
                  }}>
                  <Box
                    sx={{
                      width: '100%',
                      py: 0,
                      px: 1,
                      border: '1px solid #d2d2d2',
                      borderRadius: '30px',
                      cursor: 'default',
                      bgcolor: 'rgba(255, 152, 0, 0.08)',
                    }}>
                    <Box display='flex' alignItems='center' gap={2} p='5px' borderRadius='30px'>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: '#ff9800',
                          border: '2px solid',
                          borderColor: 'background.paper',
                        }}
                      />
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {agent.name || 'Unknown'}
                      </Typography>
                      <Typography
                        component='span'
                        variant='body2'
                        color='text.secondary'
                        sx={{ display: 'block', fontSize: '0.75rem', mt: 0.25 }}>
                        {phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* Agents Section Header */}
      {agentsOnCall.length > 0 && (
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Typography
            variant='subtitle2'
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
            }}>
            Agents
          </Typography>
        </Box>
      )}

      {/* Agent List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}>
            <CircularProgress size={40} />
          </Box>
        ) : filteredAgents.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              px: 3,
            }}>
            <Typography variant='body2' color='text.secondary' align='center'>
              {searchTerm ? 'No agents found matching your search' : 'No agents available'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, px: 1.5 }}>
            {filteredAgents.map((agent) => {
              const statusColor = getStatusColor(agent.status);
              const phoneNumber =
                agent.trackingNumbers?.incoming?.[0]?.number ||
                agent.trackingNumbers?.outgoing?.[0]?.number ||
                agent.phoneNumber ||
                '';

              return (
                <ListItem
                  key={agent.id}
                  disablePadding
                  sx={{
                    pt: '0',
                    pb: '0',
                    pl: '5px',
                    pr: '5px',
                    mb: '8px',
                  }}>
                  <ListItemButton
                    disabled={
                      agent.status !== AgentAvailabilityStatus.AVAILABLE ||
                      (selectedAgent !== null && selectedAgent.id !== agent.id) ||
                      showConfirmModal ||
                      showTransferStatusModal
                    }
                    onClick={() => handleAgentClick(agent)}
                    selected={selectedAgent?.id === agent.id}
                    sx={{
                      py: 0,
                      px: 1,
                      border: '1px solid #d2d2d2',
                      borderRadius: '30px',
                      '&:hover': {
                        border: '1px solid hsl(192, 53.70%, 47.50%)',
                        borderRadius: '30px',
                        boxShadow: '0 2px 12px 0 rgba(26, 102, 122, 0.10)',
                        bgcolor: 'white',
                      },
                      '&.Mui-selected': {
                        border: '2px solid #1A667A',
                        bgcolor: 'rgba(26, 102, 122, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(26, 102, 122, 0.12)',
                          border: '2px solid #1A667A',
                        },
                      },
                    }}>
                    <ListItemText
                      primary={
                        <Box display='flex' alignItems='center' gap={2} p='5px' borderRadius='30px'>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: statusColor,
                              border: '2px solid',
                              borderColor: 'background.paper',
                            }}
                          />
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {agent.name || 'Unknown'}
                          </Typography>
                          <Typography
                            component='span'
                            variant='body2'
                            color='text.secondary'
                            sx={{ display: 'block', fontSize: '0.75rem', mt: 0.25 }}>
                            {phoneNumber}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Transfer Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          padding: '20px 50px',
          minHeight: '50px',
        }}>
        <Button
          variant='contained'
          fullWidth
          startIcon={
            <PhoneForwarded
              sx={{
                color:
                  !selectedAgent || showConfirmModal || showTransferStatusModal
                    ? '#9e9e9e'
                    : '#1A667A',
                transition: 'color 0.2s',
              }}
            />
          }
          onClick={handleBlindTransferClick}
          disabled={!selectedAgent || showConfirmModal || showTransferStatusModal}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '25px',
            textTransform: 'none',
            width: '80px',
            backgroundColor: '#F5F5F5 !important',
            color:
              !selectedAgent || showConfirmModal || showTransferStatusModal ? '#9e9e9e' : '#1A667A',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#e6e6e6 !important',
            },
          }}>
          <span
            style={{
              color:
                !selectedAgent || showConfirmModal || showTransferStatusModal
                  ? '#9e9e9e'
                  : '#1A667A',
            }}>
            Blind
          </span>
        </Button>

        <Button
          variant='contained'
          fullWidth
          disabled={!selectedAgent || showConfirmModal || showTransferStatusModal}
          startIcon={
            <PhoneForwarded
              sx={{
                color:
                  !selectedAgent || showConfirmModal || showTransferStatusModal
                    ? '#9e9e9e'
                    : '#1A667A',
                transition: 'color 0.2s',
              }}
            />
          }
          onClick={handleWarmTransferClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '25px',
            textTransform: 'none',
            width: '80px',
            backgroundColor: '#F5F5F5 !important',
            color:
              !selectedAgent || showConfirmModal || showTransferStatusModal ? '#9e9e9e' : '#1A667A',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#e6e6e6 !important',
            },
          }}>
          <span
            style={{
              color:
                !selectedAgent || showConfirmModal || showTransferStatusModal
                  ? '#9e9e9e'
                  : '#1A667A',
            }}>
            Warm
          </span>
        </Button>
      </Box>

      {/* Transfer Confirmation Modal */}
      {selectedAgent && pendingTransferType && (
        <TransferConfirmationModal
          open={showConfirmModal}
          agent={selectedAgent}
          transferType={pendingTransferType}
          onConfirm={handleConfirmTransfer}
          onCancel={handleCancelTransfer}
        />
      )}

      {showTransferStatusModal && transferData && (
        <TransferStatusModal
          open={true}
          agent={{
            name: transferData.targetAgent.name,
          }}
          onHangup={cancelTransfer}
          isError={isTransferError}
          errorMessage={isTransferError ? 'The agent is not available at this moment' : undefined}
          onClose={handleCloseTransferModal}
        />
      )}
    </Box>
  );
};
